import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getAuthHeaders } from '../supabaseClient';
import Icon from './Icon';
import { computePotential, potentialAmountText } from './heroSelection';

function HistoryScreen({ onBack, onReopen, session }) {
    const [audits, setAudits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('All');
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [totalValueTiers, setTotalValueTiers] = useState(null);
    const [totalCount, setTotalCount] = useState(null);
    const [totalFindings, setTotalFindings] = useState(null);
    const [knownDocTypes, setKnownDocTypes] = useState([]);

    const fetchHistory = useCallback(async (currentOffset = 0, activeFilter = 'All') => {
        const params = new URLSearchParams();
        if (currentOffset > 0) params.set('offset', String(currentOffset));
        if (activeFilter !== 'All') params.set('document_type', activeFilter);
        const qs = params.toString();
        const url = `https://tm-audit-platform-production.up.railway.app/api/audit-history${qs ? '?' + qs : ''}`;

        if (currentOffset > 0) {
            setLoadingMore(true);
        } else {
            setLoading(true);
        }

        const res = await fetch(url, { headers: await getAuthHeaders() });
        const json = await res.json();
        const rows = (json.rows || []).filter(a => a.created_at);

        setTotalCount(json.total_count ?? 0);
        setTotalFindings(json.total_findings ?? 0);
        // Objective 1: graded portfolio totals. Absent on an older backend, in which
        // case the bar falls back to the Verified-only figure it has always shown.
        setTotalValueTiers(json.total_value_tiers ?? null);

        // Accumulate known doc types from unfiltered loads so filter buttons stay visible
        if (activeFilter === 'All') {
            const types = [...new Set(rows.map(a => a.document_type).filter(Boolean))];
            setKnownDocTypes(prev => [...new Set([...prev, ...types])]);
        }

        if (currentOffset > 0) {
            setAudits(prev => [...prev, ...rows]);
            setOffset(currentOffset);
            setLoadingMore(false);
        } else {
            setAudits(rows);
            setOffset(0);
            setLoading(false);
        }

        if (rows.length < 50) setHasMore(false);
    }, []);

    useEffect(() => {
        setHasMore(true);
        setFilter('All');
        setKnownDocTypes([]);
        fetchHistory(0, 'All');
    }, [session, fetchHistory]);

    // Assign per-job sequence numbers over the loaded set (chronological = oldest first).
    const numberedAudits = useMemo(() => {
        const jobCounts = {};
        return [...audits].reverse().map(row => {
            const key = row.job_name;
            jobCounts[key] = (jobCounts[key] || 0) + 1;
            return { ...row, audit_number: jobCounts[key] };
        }).reverse();
    }, [audits]);

    const fmt = (n) => n != null ? '$' + parseFloat(n).toLocaleString('en-US', { minimumFractionDigits: 2 }) : '—';
    const fmtDate = (d) => {
        if (!d) return '—';
        const date = new Date(d);
        const now = new Date();
        const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

        // Compare LOCAL calendar dates, not elapsed milliseconds.
        const startOfDay = (dt) => { const x = new Date(dt); x.setHours(0,0,0,0); return x; };
        const diffDays = Math.round((startOfDay(now) - startOfDay(date)) / 86400000);

        if (diffDays === 0) return 'Today · ' + timeStr;
        if (diffDays === 1) return 'Yesterday · ' + timeStr;
        if (diffDays < 7) return diffDays + ' days ago · ' + timeStr;
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const docTypes = ['All', ...knownDocTypes];

    return (
        <div className="history-screen">
            <div className="history-back-row">
                <button className="history-back-link" onClick={onBack}><Icon name="arrow-left" size={14} /> Back</button>
            </div>
            <div className="history-header">
                <div className="history-title">
                    <span className="logo-audee">Audee<span className="logo-dot">.</span></span> History
                </div>
            </div>

            {!loading && numberedAudits.length > 0 && (
                <div className="history-stats-bar">
                    <div className="history-stat">
                        <div className="history-stat-value">{totalCount ?? numberedAudits.length}</div>
                        <div className="history-stat-label">Audits</div>
                    </div>
                    <div className="history-stat-divider" />
                    {/* POTENTIAL-FIRST (Decision Log 2026-08-13). Mirrors the home screen
                        and the report hero: the portfolio stat leads with the bounded
                        POTENTIAL — Verified + Likely + Conditional — so a book of findings
                        never aggregates to a figure smaller than the reports that compose
                        it. Always a floor ("+"): a portfolio sum spans many estimates, so
                        there is no single grand total to bound it against and no per-row
                        precision gate to read (computePotential → unbounded → imprecise).
                        Fails closed to '—' when the backend returns no graded totals —
                        never the legacy ungated total_dollar_impact (that aggregate once
                        read $11.8M portfolio-wide). fmt(null) → '—'. */}
                    <div className="history-stat" title="What Audee identified across your book — Verified (bankable now), Likely (rate to confirm), and Conditional (docs or pricing owed).">
                        <div className="history-stat-value opportunity-amount">
                            {totalValueTiers
                                ? potentialAmountText(computePotential({
                                    verifiedTotal: totalValueTiers.verified,
                                    likelyTotal: totalValueTiers.likely,
                                    conditionalTotal: totalValueTiers.conditional,
                                    conditionalPrecise: false,
                                }), fmt)
                                : fmt(null)}
                        </div>
                        <div className="history-stat-label">Total potential identified</div>
                    </div>
                    {totalValueTiers && totalValueTiers.conditional > 0 && (
                        <>
                            <div className="history-stat-divider" />
                            {/* A BREAKDOWN of the figure above, not a second total — the
                                label says "of which" now that conditional is inside it. */}
                            <div className="history-stat" title="Part of the total above that still needs supporting documentation, vendor pricing, or scope confirmation.">
                                <div className="history-stat-value conditional-amount">~{fmt(totalValueTiers.conditional)}</div>
                                <div className="history-stat-label">Of which needs docs/pricing</div>
                            </div>
                        </>
                    )}
                    <div className="history-stat-divider" />
                    <div className="history-stat">
                        <div className="history-stat-value">{totalFindings ?? 0}</div>
                        <div className="history-stat-label">Total findings</div>
                    </div>
                </div>
            )}

            {!loading && docTypes.length > 2 && (
                <div className="history-filters">
                    {docTypes.map(t => (
                        <button
                            key={t}
                            className={`history-filter-btn ${filter === t ? 'active' : ''}`}
                            onClick={() => {
                                setFilter(t);
                                setAudits([]);
                                setOffset(0);
                                setHasMore(true);
                                fetchHistory(0, t);
                            }}
                        >
                            {t}
                        </button>
                    ))}
                </div>
            )}

            {loading && (
                <div className="history-loading">
                    <div className="spinner" />
                </div>
            )}

            {!loading && numberedAudits.length === 0 && (
                <div className="history-empty">
                    <div className="history-empty-icon"><Icon name="circle-dashed" size={36} /></div>
                    <div className="history-empty-title">No audits yet</div>
                    <div className="history-empty-sub">Run your first audit to start tracking findings.</div>
                    <button className="action-btn" onClick={onBack}>Run audit</button>
                </div>
            )}

            {!loading && numberedAudits.length > 0 && (
                <div className="history-list">
                    {numberedAudits.map(audit => (
                        <div key={audit.id} className="history-card" onClick={() => onReopen(audit.id)}>
                            <div className="history-card-top">
                                <div className="history-card-left">
                                    <div className="history-job-name">
                                        {audit.job_name}
                                        <span className="history-audit-num">#{audit.audit_number}</span>
                                        {audit.document_type && (
                                            <span className={`history-doc-type ${audit.document_type.includes('Xactimate') ? 'xactimate' : audit.document_type === 'Cotality' ? 'cotality' : audit.document_type.includes('T&M') ? 'tm' : ''}`}>
                                                {audit.document_type.includes('Xactimate') ? 'Xactimate' : audit.document_type === 'Cotality' ? 'Cotality' : audit.document_type.includes('T&M') ? 'T&M' : audit.document_type}
                                            </span>
                                        )}
                                    </div>
                                    <div className="history-meta">{fmtDate(audit.created_at)}{audit.grand_total ? ` · ${fmt(audit.grand_total)} estimate` : ''}</div>
                                </div>
                                <div className="history-card-right">
                                    {/* Row headline = the same bounded POTENTIAL the report
                                        hero shows for this job, so opening a row can never
                                        contradict the row that led to it. A row with findings
                                        never renders '—' or $0 as its lead. */}
                                    <div className="history-impact-block">
                                        {(() => {
                                            // A document Audee did not read (read_quality = the
                                            // saved readQuality verdict, GAP-089) never leads with a
                                            // dollar, a count or "No findings": none of its checks ran.
                                            if (audit.read_quality?.underRead) {
                                                return (<>
                                                    <div className="history-impact-amount">Not read</div>
                                                    <div className="history-impact-label">no audit performed</div>
                                                </>);
                                            }
                                            const tiers = audit.value_tiers;
                                            const findingsCount = audit.findings_count || 0;
                                            // Fail closed: with no value_tiers, show '—', never the
                                            // legacy dollar_impact — that ungated figure predates every
                                            // grounding/precision gate and once displayed $3.18M. The
                                            // em-dash means MISSING data, never "nothing bankable yet".
                                            if (!tiers) {
                                                return (<>
                                                    <div className="history-impact-amount">—</div>
                                                    <div className="history-impact-label">Not graded</div>
                                                </>);
                                            }
                                            const verified = tiers.verified || 0;
                                            // Per-row we hold the audit id and its grand total, so the
                                            // quarantine and half-estimate bounds both apply here.
                                            // conditionalPrecise is false because the row does not carry
                                            // the per-finding precision gate — the figure reads as a floor.
                                            const potential = computePotential({
                                                verifiedTotal: verified,
                                                likelyTotal: tiers.likely,
                                                conditionalTotal: tiers.conditional,
                                                grandTotal: audit.grand_total,
                                                auditId: audit.id,
                                                conditionalPrecise: false,
                                            });
                                            if (potential.total > 0) {
                                                return (<>
                                                    <div className={'history-impact-amount' + (verified > 0 ? '' : ' likely')}>
                                                        {potentialAmountText(potential, fmt)}
                                                    </div>
                                                    <div className="history-impact-label">
                                                        {verified > 0 ? `incl. ${fmt(verified)} bill-now` : 'potential — confirm rates'}
                                                    </div>
                                                </>);
                                            }
                                            // Findings fired but nothing survived the bound (or the row
                                            // is quarantined): lead with the COUNT, never a $0 or a dash.
                                            if (findingsCount > 0) {
                                                return (<>
                                                    <div className="history-impact-amount">{findingsCount} found</div>
                                                    <div className="history-impact-label">pending validation</div>
                                                </>);
                                            }
                                            return (<>
                                                <div className="history-impact-amount">—</div>
                                                <div className="history-impact-label">No findings</div>
                                            </>);
                                        })()}
                                    </div>
                                    <button className="history-reopen-btn" onClick={(e) => { e.stopPropagation(); onReopen(audit.id); }}>
                                        Open <Icon name="arrow-right" size={13} />
                                    </button>
                                </div>
                            </div>
                            <div className="history-card-bottom">
                                {audit.read_quality?.underRead ? (
                                    <div className="history-findings-count">Not read</div>
                                ) : (<>
                                    <div className="history-severity-row">
                                        {audit.critical_count > 0 && <span className="hsev critical">{audit.critical_count} critical</span>}
                                        {audit.high_count > 0 && <span className="hsev high">{audit.high_count} high</span>}
                                        {audit.medium_count > 0 && <span className="hsev medium">{audit.medium_count} medium</span>}
                                        {audit.low_count > 0 && <span className="hsev low">{audit.low_count} low</span>}
                                    </div>
                                    <div className="history-findings-count">{audit.findings_count} findings</div>
                                </>)}
                            </div>
                        </div>
                    ))}

                    {hasMore && numberedAudits.length > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'center', margin: '16px 0' }}>
                            <button
                                className="action-btn secondary"
                                disabled={loadingMore}
                                onClick={() => fetchHistory(offset + 50, filter)}
                            >
                                {loadingMore ? 'Loading...' : 'Load more'}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default HistoryScreen;
